// ManageStoresPanel — the Stores section's tile grid (My Account C1, task
// T7b, design.md AC7; rewritten by the account-area redesign Task 7,
// Amendment 2026-10-02 F8/O9).
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 7). ORIGINAL: a
// "Manage Stores" page — an h2, a page-swap to StoreForm for add/edit, and
// an INLINE delete confirmation (text + danger button + Cancel link in the
// card row; embed-shared had no modal primitive when that convention was
// set). CURRENT: the amendment's tile presentation — each tile carries the
// store's FULL address (line1 and line2 as separate lines, then
// "City, ST ZIP") and the products assigned to it ("No products assigned
// yet" when none); add/edit/remove are TRIGGERS here and the dialogs live in
// StoresPanel (EmbedDialog exists now — the inline-confirm workaround for
// its absence retires with it). The "Manage Stores" h2 is dropped: the
// section's page title already names it (the account shell renders one h1/h2
// per section), and a second heading for the same thing is noise.
//
// ⚠️ AMENDED 2026-10-06 (account-area redesign, Task 18, migration 145).
// Tiles gained the store's phone (under the address) and an ORDERS block:
// the newest STORE_TILE_ORDERS_LIMIT orders as compact OrderCardList cards
// (ruling S4-A — tiles show orders, not just assignments), each order number
// opening the order in the shell via onOpenOrder, plus a per-store
// "View all orders for <name>" control that deep-links to the Orders
// section filtered to this store. The three per-tile order states:
//   - key ABSENT from ordersByStore (still loading, or that store's fetch
//     FAILED) → the block stays silent — never a false "no orders" claim;
//   - key present, empty array → "No orders for this store yet";
//   - key present with rows → compact cards (sliced to the limit here too —
//     the server already capped the fetch, but the slice makes the tile's
//     contract local) + the View all control.
//
// Presentational only — every mutation is a callback into StoresPanel, which
// owns the stores state and the If-Match delete.
//
// Inline styles only — no Tailwind. Every <button> is explicitly
// type="button" (none of these sit inside a form).

import { useEmbedTokens, EmbedButton, EmbedCard, EmbedText } from "../../embed-shared/primitives";
import type { OrderSummary } from "../../embed-shared/types";
import type { CustomerStoreWire } from "./store-api";
import { STORE_TILE_ORDERS_LIMIT } from "./store-api";
import { OrderCardList } from "./OrderCardList";

export interface ManageStoresPanelProps {
  stores: CustomerStoreWire[];
  /** storeId -> the products held at that store, grouped by StoresPanel from
   *  GET /me/owned-products. `quantity` is the units at this store for a
   *  one-time item, and null for a subscription item (no per-store count). A
   *  MISSING key means no products at that store; undefined means the list is
   *  hidden by permissions, so no product line renders at all. */
  productsByStore: Record<string, { name: string; quantity: number | null }[]> | undefined;
  /** storeId -> the store's newest orders, fetched by StoresPanel (already
   *  capped at STORE_TILE_ORDERS_LIMIT). A MISSING key means "loading, or
   *  that store's fetch failed" — the orders block stays silent for it. */
  ordersByStore: Record<string, OrderSummary[]>;
  /** Opens an order (the compact cards' order-number control). */
  onOpenOrder: (orderId: string) => void;
  /** Deep-link to the Orders section filtered to this store (the View all
   *  control; wired to the shell by StoresPanel/AccountCenter). */
  onViewStoreOrders: (store: CustomerStoreWire) => void;
  onAdd: () => void;
  onEdit: (store: CustomerStoreWire) => void;
  /** Ask StoresPanel to open the remove confirmation for this store. */
  onRemove: (store: CustomerStoreWire) => void;
}

export function ManageStoresPanel({
  stores,
  productsByStore,
  ordersByStore,
  onOpenOrder,
  onViewStoreOrders,
  onAdd,
  onEdit,
  onRemove,
}: ManageStoresPanelProps) {
  const t = useEmbedTokens();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: t.spacing.md }}>
        <EmbedButton type="button" variant="secondary" size="sm" onClick={onAdd}>
          + Add store
        </EmbedButton>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
        {stores.map((store) => {
          const products = productsByStore?.[store.id] ?? [];
          // undefined key = loading or failed (silent); [] = loaded, none.
          const storeOrders = ordersByStore[store.id];
          return (
            <div key={store.id} data-testid={`store-card-${store.id}`}>
              <EmbedCard padding={t.spacing.md}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: t.spacing.sm, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: t.fontSize.sm, fontWeight: t.fontWeight.strong, color: t.colors.text }}>
                      {store.name}
                    </div>
                    {/* F8: the FULL address. line1/line2 render as separate
                        lines (never comma-joined into one string); the
                        locality line is the conventional "City, ST ZIP".
                        EmbedText is an inline span, so each line gets a block
                        wrapper to actually break. */}
                    {store.line1 && (
                      <div><EmbedText tone="secondary">{store.line1}</EmbedText></div>
                    )}
                    {store.line2 && (
                      <div><EmbedText tone="secondary">{store.line2}</EmbedText></div>
                    )}
                    {(store.city || store.state || store.postal_code) && (
                      <div>
                        <EmbedText tone="secondary">
                          {[store.city, store.state].filter(Boolean).join(", ")}
                          {store.city || store.state ? " " : ""}
                          {store.postal_code ?? ""}
                        </EmbedText>
                      </div>
                    )}
                    {/* Task 18: the store's contact phone, format-light —
                        rendered verbatim, never re-formatted. */}
                    {store.phone && (
                      <div><EmbedText tone="secondary">{store.phone}</EmbedText></div>
                    )}
                    {productsByStore !== undefined && <div style={{ marginTop: t.spacing.xs }}>
                      {products.length > 0 ? (
                        products.map((p, i) => (
                          <div key={`${p.name}-${i}`}>
                            <EmbedText tone="secondary">
                              {p.quantity != null && p.quantity > 1 ? `${p.name} ×${p.quantity}` : p.name}
                            </EmbedText>
                          </div>
                        ))
                      ) : (
                        <EmbedText tone="secondary">No products assigned yet</EmbedText>
                      )}
                    </div>}
                    {/* Task 18: the tile's ORDERS block — newest orders as
                        compact cards + the per-store View all escape hatch.
                        See the header for the three key-states. */}
                    {storeOrders !== undefined && (
                      <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
                        <div style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Orders</div>
                        {storeOrders.length > 0 ? (
                          <>
                            <OrderCardList
                              orders={storeOrders.slice(0, STORE_TILE_ORDERS_LIMIT)}
                              onOpenOrder={onOpenOrder}
                              compact
                            />
                            <div>
                              <EmbedButton
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => onViewStoreOrders(store)}
                                aria-label={`View all orders for ${store.name}`}
                              >
                                View all orders
                              </EmbedButton>
                            </div>
                          </>
                        ) : (
                          <EmbedText tone="secondary">No orders for this store yet</EmbedText>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, flex: "0 0 auto" }}>
                    <EmbedButton type="button" variant="secondary" size="sm" onClick={() => onEdit(store)}>
                      Edit
                    </EmbedButton>
                    <EmbedButton type="button" variant="secondary" size="sm" onClick={() => onRemove(store)}>
                      Remove
                    </EmbedButton>
                  </div>
                </div>
              </EmbedCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}
